import { action } from '@ember/object';
import Component from '@glimmer/component';

import type { DSSetup, ValidationErrors } from 'webapp-types';

import FormMetricSelectComponent from 'webapp/components/form/metric-select';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import FormSelectUnitsComponent from 'webapp/components/form/select-units';
import RowErrorComponent from 'webapp/components/row-error';

import type { MetricSetup } from 'webapp/models-custom';

type ValidationKeys = 'metric' | 'unit' | 'attribute';

interface Args {
	metricSetup: MetricSetup;
	errors: ValidationErrors<ValidationKeys>;
	isDisabled: boolean;
	label?: string;
	// locks the metric itself while leaving dimensions/attributes/units editable,
	// for forms where the metric is fixed by the flow that opened them
	isMetricDisabled?: boolean;
}

export default class NotificationsEditFormMetricSetupRowComponent extends Component<Args> {
	get metricSetup() {
		return this.args.metricSetup;
	}

	get isMetricSelectDisabled(): boolean {
		return this.args.isDisabled || !!this.args.isMetricDisabled;
	}

	get showUnitsAtMetricRow(): boolean {
		return this.metricSetup.allowUnitNameFilter && !this.metricSetup.allowDimensions;
	}

	@action
	changeMetricData(metricData: DSSetup.MetricData): void {
		this.metricSetup.setFromMetricData(metricData);
	}

	@action
	changeDimensions(dimensions: string[]): void {
		this.metricSetup.dimensions = dimensions;
		this.metricSetup.resetFilters();
	}

	@action
	changeAttribute(attribute: string | null): void {
		this.metricSetup.setFiltersFromAttributeName(attribute);
	}

	@action
	changeUnit(unit: string | null): void {
		this.metricSetup.unitName = unit;
	}

	<template>
		<div class="row row--metric-setup">
			{{#if @label}}
				<label>{{@label}}</label>
			{{/if}}
			<div class="selector selector-metric">
				<FormMetricSelectComponent
					@selected={{this.metricSetup.metricData}}
					@onChange={{this.changeMetricData}}
					@isDisabled={{this.isMetricSelectDisabled}}
					@placeholder="Select a metric"
					@disableNotConnected={{true}}
				/>
				<RowErrorComponent @items={{@errors.metric}} />
			</div>

			{{#if this.showUnitsAtMetricRow}}
				<div class="selector selector-item">
					<FormSelectUnitsComponent
						@selected={{this.metricSetup.unitName}}
						@metricSetupForQuery={{this.metricSetup}}
						@onChange={{this.changeUnit}}
						@placeholder="Unit"
						@isMultiple={{false}}
						@isRequired={{true}}
						@maxSelectionLength={{1}}
						@isDisabled={{@isDisabled}}
						@allowAllUnits={{false}}
					/>
					<RowErrorComponent @items={{@errors.unit}} />
				</div>
			{{/if}}

			{{#if this.metricSetup.allowDimensions}}
				<div class="selector selector-item">
					<FormSelectDimensionsComponent
						@metricSetup={{this.metricSetup}}
						@onChange={{this.changeDimensions}}
						@isDisabled={{@isDisabled}}
					/>
				</div>

				{{#if this.metricSetup.allowBasicDimensionValueFilters}}
					<div class="selector selector-item">
						<FormSelectAttributesComponent
							@selected={{this.metricSetup.firstAttributeNameFromBasicFilters}}
							@metricSetupForQuery={{this.metricSetup}}
							@onChange={{this.changeAttribute}}
							@isMultiple={{false}}
							@isRequired={{true}}
							@maxSelectionLength={{1}}
							@isDisabled={{@isDisabled}}
						/>
						<RowErrorComponent @items={{@errors.attribute}} />
					</div>
				{{/if}}

				{{#if this.metricSetup.allowUnitNameFilter}}
					{{#unless this.showUnitsAtMetricRow}}
						<div class="selector selector-item">
							<FormSelectUnitsComponent
								@selected={{this.metricSetup.unitName}}
								@metricSetupForQuery={{this.metricSetup}}
								@onChange={{this.changeUnit}}
								@placeholder="Unit"
								@isMultiple={{false}}
								@isRequired={{true}}
								@maxSelectionLength={{1}}
								@isDisabled={{@isDisabled}}
								@allowAllUnits={{false}}
							/>
							<RowErrorComponent @items={{@errors.unit}} />
						</div>
					{{/unless}}
				{{/if}}

			{{/if}}
		</div>
	</template>
}
